<template>
    <div>
        <button
            type="button"
            class="rounded-2xl z-[2] sticky top-4 border border-solid border-white shadow-lg flex w-full items-center justify-between gap-2 px-4 py-3 text-start bg-white/50 backdrop-blur-sm cursor-pointer border-t-0 hover:bg-white/80 transition"
            :class="{ 'animate-inbox-blink': highlighted }"
            data-test="section-toggle"
            @click="active = !active"
        >
            <div class="flex justify-between grow items-center gap-2">
                <span class="text-sm font-bold text-slate-700">{{ props.section.label }}</span>
                <template v-if="showCount">
                    <vx-icon-loading v-if="props.loadingCounts" class="w-2.5 h-2.5" spin />
                    <vx-badge
                        v-else-if="props.sectionCount > 0"
                        class="min-w-5 text-center"
                        :color="props.section.badgeColor"
                        size="2xs"
                    >
                        {{ props.sectionCount }}
                    </vx-badge>
                </template>
            </div>
            <font-awesome-icon
                widthAuto
                class="text-xs text-slate-400 transition-transform"
                :class="{ 'rotate-180': active }"
                :icon="faChevronDown"
            ></font-awesome-icon>
        </button>
        <div v-show="active" data-test="section-content" class="pt-4 pb-6 flex flex-col gap-2">
            <div v-if="props.loadingCategories" class="flex flex-col gap-2">
                <div
                    v-for="n in 3"
                    :key="n"
                    :style="{ animationDelay: `${n * 0.2}s` }"
                    class="rounded-lg animate-pulse bg-slate-300 h-10 w-full"
                ></div>
            </div>
            <vx-box v-else-if="allCategoriesEmpty" color="info" data-test="empty-section-message">
                <p class="text-sm m-0 px-3 py-2">
                    No conversations in <strong>{{ props.section.label }}</strong> right now. They'll show up here once
                    conversations are classified into this section.
                </p>
            </vx-box>
            <intelligent-inbox-category
                v-for="category in visibleCategories"
                v-else
                :key="category.id"
                :category="category"
                :sectionValue="props.section.sectionValue"
                :loadingCounts="isDoneSection ? false : props.loadingCounts"
                :count="isDoneSection ? 0 : props.categoryCounts[String(category.id ?? 'uncategorized')] ?? 0"
                :highlighted="highlighted"
                :autoExpand="visibleCategories.length === 1"
            ></intelligent-inbox-category>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBadge, VxBox, VxIconLoading } from '@voxie/frontend-components';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useIntelligentInbox, type IntelligentSection } from './useIntelligentInbox';
import type { ThreadSectionValue } from '~/constants/messageHub';
import IntelligentInboxCategory from './IntelligentInboxCategory.vue';
import Bus from '~/globals/eventBus';

const props = defineProps<{
    section: IntelligentSection;
    loadingCategories: boolean;
    loadingCounts: boolean;
    sectionCount: number;
    categoryCounts: Record<string, number>;
}>();

const { activeSection } = useIntelligentInbox();

const isDoneSection = computed(() => props.section.sectionValue === 'completed');
const showCount = computed(() => !isDoneSection.value);

const active = computed({
    get: () => activeSection.value === props.section.sectionValue,
    set: (value: boolean) => {
        activeSection.value = value ? props.section.sectionValue : null;
    },
});

const highlighted = ref(false);
let highlightTimer: ReturnType<typeof setTimeout> | undefined;

const visibleCategories = computed(() =>
    isDoneSection.value
        ? props.section.categories
        : props.section.categories.filter((category) => {
              const key = String(category.id ?? 'uncategorized');
              return (props.categoryCounts[key] ?? 0) > 0;
          })
);

const allCategoriesEmpty = computed(() => {
    if (isDoneSection.value || props.loadingCounts || props.loadingCategories) {
        return false;
    }
    return visibleCategories.value.length === 0;
});

const handleFocusSection = (sectionValue: ThreadSectionValue): void => {
    if (sectionValue !== props.section.sectionValue) {
        return;
    }

    active.value = true;
    clearTimeout(highlightTimer);
    highlighted.value = true;
    highlightTimer = setTimeout(() => {
        highlighted.value = false;
    }, 1200);
};

onMounted(() => {
    Bus.$on('intelligentInbox:focusSection', handleFocusSection);
});

onUnmounted(() => {
    Bus.$off('intelligentInbox:focusSection', handleFocusSection);
    clearTimeout(highlightTimer);
});
</script>
